import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bus, Users, Plane, Building2, CalendarCheck, Radio, ShieldCheck, Phone, Ship, Landmark, Hotel, MapPinned, Gauge, ClipboardCheck } from 'lucide-react';
import { SectionHeader } from '@/components/home/CoreSections';
import { StatStrip } from '@/components/brand/BrandKit';
import { FinalCta } from '@/components/home/TrustSectionsV2';
import { FormEmbedSection } from '@/components/FormEmbedSection';
import { CHAUFFEUR_FORM_ID } from '@/components/LeadConnectForm';

const IMG = {
  hero: "/generated/hero-coaches.jpg",
  boarding: "/brand/coach-boarding-dubai.jpg",
  fleet: "/managed/coach-fleet-lineup.jpg",
  highway: "/managed/coach-highway-dubai.jpg",
  telematics: "/managed/telematics-control-room.jpg",
  coach: "/brand/coach-dubai.jpg",
};

const STATS = [
  { value: "800", label: "Buses & coaches" },
  { value: "800+", label: "RTA-approved drivers" },
  { value: "5,000+", label: "Daily trips" },
  { value: "24/7", label: "Operations & control room" },
];

const SECTORS = [
  { icon: Plane, title: "Airlines" },
  { icon: Hotel, title: "Hospitality" },
  { icon: MapPinned, title: "DMCs & Tour Operators" },
  { icon: CalendarCheck, title: "Event Organisers" },
  { icon: Ship, title: "Cruise Companies" },
  { icon: Landmark, title: "Government Entities" },
];

const SERVICES = [
  { icon: Users, title: "Staff & Labour Transportation", desc: "Scheduled shuttle routes for corporates, hospitality groups, industrial zones and free-zone operators, with route optimisation and attendance reporting." },
  { icon: Plane, title: "Airline Crew & Airport Transfers", desc: "A trusted crew-transportation partner to major airlines, with supporting airport desks at DXB Terminals 1, 2, 3 and Al Maktoum International Airport." },
  { icon: Building2, title: "Government & Municipal Contracts", desc: "Long-term managed transport programmes for government entities, delivered with RTA-approved drivers and full compliance." },
  { icon: CalendarCheck, title: "Events, Delegations & Tourism", desc: "Luxury coaches for conferences, exhibitions, sporting events, cruise operators and DMCs, from a single coach to a 200-vehicle fleet." },
  { icon: Radio, title: "Fully Automated Operations & Tracking", desc: "A web-based operations system with live GPS tracking and allocation gives clients real-time visibility of every vehicle and every trip." },
  { icon: ShieldCheck, title: "Safety-First, Fully Owned Fleet", desc: "New, well-maintained and fully owned buses and coaches, with frequent safety check-ups and A-grade in-house workshops." },
];

const FLEET = [
  { name: "Executive Vans", seats: "Toyota Hiace or similar · 7 to 14 seats", desc: "Premium vans for VIP groups, crew transport, site visits and airport meet-and-greet." },
  { name: "Mini Buses", seats: "Toyota Coaster or similar · up to 30 seats", desc: "Ideal for hotel shuttles, crew transport and campus routes across the Emirates." },
  { name: "Luxury Buses", seats: "49 seats", desc: "High-capacity, air-conditioned buses built for daily staff and labour movements." },
  { name: "Luxury Coaches", seats: "49 to 53 seats", desc: "Reclining seats, USB charging and onboard comfort for delegations and long-distance travel." },
];

const MONITORING = [
  "Airport permits and airside compliance",
  "Round-the-clock monitoring and vehicle check-ups",
  "Driving-behaviour tracking: harsh braking, over-speeding, sudden acceleration and deviation",
  "Daily check-up reporting to the quality department",
];

export default function BusCoachPage() {
  useEffect(() => { document.title = "Royal Limousine Coaches | Bus & Coach Transportation UAE | Eurogulf Mobility Group"; }, []);

  return (
    <div data-testid="bus-coach-page">
      <section data-testid="bus-coach-hero" className="relative overflow-hidden bg-[#121212] text-white">
        <img src={IMG.hero} alt="Eurogulf Mobility Group luxury coach on a UAE highway" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <img src="/brands/royal-limo-white.webp" alt="Royal Limousine Coaches" className="h-10 sm:h-11 w-auto mb-6" />
          <span className="chip-orange">Royal Limousine Coaches</span>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-white max-w-3xl">Bus &amp; coach transportation, run like an airline.</h1>
          <p className="mt-5 font-body text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">One of the biggest and youngest fleets of luxury buses and coaches in the market, moving airline crews, corporate staff, government teams and event delegations across the UAE, every day since 1998.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" data-testid="bus-coach-cta-quote" className="btn-primary">Request a Transport Proposal <ArrowRight className="w-4 h-4" /></Link>
            <a href="tel:800364" dir="ltr" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#121212]"><Phone className="w-4 h-4" /> 800 364</a>
          </div>
        </div>
      </section>

      <StatStrip stats={STATS} testid="bus-coach-stats" />

      <section className="bg-[#FAFAFA] py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMG.boarding} alt="Guests boarding a Eurogulf coach in Dubai" loading="lazy" className="h-64 w-full rounded-xl object-cover col-span-2 border border-black/[0.06]" />
            <img src={IMG.fleet} alt="Eurogulf Mobility Group coach fleet lined up at dawn" loading="lazy" className="h-44 w-full rounded-xl object-cover border border-black/[0.06]" />
            <img src={IMG.highway} alt="Eurogulf Mobility Group coach on a Dubai highway" loading="lazy" className="h-44 w-full rounded-xl object-cover border border-black/[0.06]" />
          </div>
          <div>
            <SectionHeader compact chip="Who We Move" title="The transport backbone behind the UAE's biggest teams." desc="Royal Limousine Coaches began in 1998 as the passenger-transport arm of Eurogulf Mobility Group. Today it is one of the largest privately operated bus and coach fleets in the country, trusted by airlines, hotels, developers and government." />
            <ul className="mt-8 space-y-3">
              {["Average fleet size of 800 buses and coaches", "More than 800 RTA-approved drivers", "5,000+ daily trips across the UAE", "Supporting airport desks at DXB T1, T2, T3 & Al Maktoum International"].map((t) => (
                <li key={t} className="flex items-start gap-3 font-body text-sm text-[#121212]"><span className="mt-1.5 h-2 w-2 rounded-sm bg-[#EE5A01] flex-shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="bus-coach-sectors" className="bg-white py-14 sm:py-20 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Sectors We Serve" title="Mass transportation for every industry." align="center" />
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {SECTORS.map((s) => (
              <div key={s.title} className="bg-white p-5 flex flex-col items-center text-center transition-colors hover:bg-[#FFF4ED]/70">
                <s.icon className="w-5 h-5 text-[#EE5A01]" />
                <p className="mt-3 font-heading font-bold text-xs sm:text-sm text-[#121212] leading-tight">{s.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="bus-coach-services" className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Services" title="Managed transport, end to end." align="center" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {SERVICES.map((s) => (
              <div key={s.title} className="bg-white p-6 lg:p-7 transition-colors hover:bg-[#FFF4ED]/70">
                <s.icon className="w-5 h-5 text-[#EE5A01]" />
                <h3 className="mt-5 font-heading font-bold text-base text-[#121212]">{s.title}</h3>
                <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="bus-coach-fleet" className="relative bg-[#F5F5F3] py-14 sm:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionHeader compact chip="The Fleet" title="From executive vans to 53-seat luxury coaches." desc="A new, well-maintained and fully owned fleet, GPS-monitored and connected, renewed on strict age cycles so your passengers always travel in modern, air-conditioned comfort." />
            <img src={IMG.coach} alt="Luxury coach on Sheikh Zayed Road, Dubai" loading="lazy" className="mt-8 h-56 w-full rounded-xl object-cover border border-black/[0.06]" />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {FLEET.map((v) => (
              <div key={v.name} className="bg-white p-6 flex flex-col">
                <Bus className="w-5 h-5 text-[#EE5A01]" />
                <h3 className="mt-4 font-heading font-bold text-base text-[#121212]">{v.name}</h3>
                <p className="font-heading font-bold text-[11px] text-[#EE5A01] tracking-wider uppercase mt-1">{v.seats}</p>
                <p className="mt-3 font-body text-sm text-[#666666] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="bus-coach-operations" className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src={IMG.telematics} alt="Eurogulf Mobility Group live fleet-tracking control room" loading="lazy" className="w-full h-56 sm:h-72 lg:h-80 rounded-xl object-cover border border-black/[0.06]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <SectionHeader compact chip="Managed Operations" title="Every trip monitored, round the clock." desc="A fully automated operation and online booking system, backed by a 24/7 operations and customer-service team, keeps every vehicle safe, tracked and accountable." />
            <ul className="mt-8 space-y-4">
              {MONITORING.map((m) => (
                <li key={m} className="flex items-start gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><Gauge className="w-4 h-4" /></span>
                  <span className="font-body text-sm text-[#333] leading-relaxed pt-1.5">{m}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            <div className="bg-white p-6">
              <ClipboardCheck className="w-5 h-5 text-[#EE5A01]" />
              <p className="mt-4 font-heading font-black text-xl text-[#121212]">Fully Owned</p>
              <p className="mt-1 font-body text-sm text-[#666666]">A fleet we own, maintain and renew in-house, not sub-contracted.</p>
            </div>
            <div className="bg-white p-6">
              <ShieldCheck className="w-5 h-5 text-[#EE5A01]" />
              <p className="mt-4 font-heading font-black text-xl text-[#121212]">ISO 9001:2015</p>
              <p className="mt-1 font-body text-sm text-[#666666]">Quality-assured processes across drivers, vehicles and dispatch.</p>
            </div>
            <div className="bg-white p-6 col-span-2">
              <Plane className="w-5 h-5 text-[#EE5A01]" />
              <p className="mt-4 font-heading font-bold text-lg text-[#121212]">Airport desks: DXB T1, T2, T3 &amp; Al Maktoum International</p>
              <p className="mt-1 font-body text-sm text-[#666666]">Dedicated coordinators supporting crew and group movements at every major Dubai terminal.</p>
            </div>
          </div>
        </div>
      </section>

      <FormEmbedSection id="enquiry" testid="buscoach-form-section" chip="Book Group Transport"
        title="Request a bus, coach or staff-transport quote."
        desc="Tell us your route, group size and schedule and our managed-transport team will prepare your proposal. For an instant coach estimate, use our online booking engine."
        formId={CHAUFFEUR_FORM_ID} formName="Chauffeur Services Form" formTestid="buscoach-services-form" showContact />

      <FinalCta />
    </div>
  );
}
